import { useEffect, useState } from 'react';
import type { IconDefinition } from '../elements/icon.ts';

// Legacy dashboards name icons as the legacy `AwesomeIcon` enum does (`home`, `solar_panel`,
// `sun_bright`). They are looked up by Font Awesome name or alias in Font Awesome Pro's classic
// regular style, kept in `regular_icons.json`, or in its light style (`light_icons.json`), which the
// solar tile's quiet cross draws with. Both are written by scripts/extract-icons.mjs from the owner's
// kit (license in FONTAWESOME-LICENSE.txt). Each is nearly 3 MB, so it is loaded on first use.

/** A classic Font Awesome style the app has. */
export type IconStyle = 'regular' | 'light';

interface IconData {
    /** Name to `[width, height, path]`. */
    icons: Record<string, [number, number, string | string[]]>;
    /** Alias to name. */
    aliases: Record<string, string>;
}

const iconIndexes = new Map<IconStyle, Promise<Map<string, IconDefinition>>>();

async function loadIconData(style: IconStyle) : Promise<IconData> {
    const module = style === 'light' ? await import('./light_icons.json') : await import('./regular_icons.json');

    return module.default as unknown as IconData;
}

async function buildIconIndex(style: IconStyle) : Promise<Map<string, IconDefinition>> {
    const data = await loadIconData(style);
    const index = new Map<string, IconDefinition>();

    for (const [name, [width, height, path]] of Object.entries(data.icons)) {
        index.set(name, { iconName: name, icon: [width, height, [], '', path] });
    }
    for (const [alias, name] of Object.entries(data.aliases)) {
        const icon = index.get(name);
        if (icon) {
            index.set(alias, icon);
        }
    }

    return index;
}

function iconIndexOf(style: IconStyle) : Promise<Map<string, IconDefinition>> {
    let index = iconIndexes.get(style);
    if (!index) {
        index = buildIconIndex(style);
        iconIndexes.set(style, index);
    }

    return index;
}

/** The icon for a legacy name (`solar_panel` becomes `solar-panel`) in `style`, or undefined. */
export async function findLegacyIcon(name: string | undefined, style: IconStyle = 'regular') : Promise<IconDefinition | undefined> {
    if (!name) {
        return undefined;
    }

    const index = await iconIndexOf(style);

    return index.get(name.replace(/_/g, '-').toLowerCase());
}

/**
 * Icon names that contain every word of `query` (Font Awesome names and aliases, `solar_panel`
 * written as `solar-panel`), each icon once, shorter names first; at most `limit`. For an icon picker.
 */
export async function searchIcons(query: string, limit = 60) : Promise<string[]> {
    const words = query.toLowerCase().replace(/_/g, '-').split(/[\s-]+/).filter((word) => word !== '');
    if (words.length === 0) {
        return [];
    }

    const index = await iconIndexOf('regular');
    const seen = new Set<IconDefinition>();
    const found: string[] = [];
    for (const [name, icon] of index) {
        if (seen.has(icon) || !words.every((word) => name.includes(word))) {
            continue;
        }
        seen.add(icon);
        found.push(icon.iconName);
    }

    return found.sort((a, b) => a.length - b.length || a.localeCompare(b)).slice(0, limit);
}

/** React hook around `findLegacyIcon`; undefined until the icon is loaded or when it is unknown. */
export function useLegacyIcon(name: string | undefined, style: IconStyle = 'regular') : IconDefinition | undefined {
    const [icon, setIcon] = useState<IconDefinition>();

    useEffect(() => {
        let isCurrent = true;
        const load = async () : Promise<void> => {
            const found = await findLegacyIcon(name, style);
            if (isCurrent) {
                setIcon(found);
            }
        };
        void load();

        return () => {
            isCurrent = false;
        };
    }, [name, style]);

    return icon;
}
